"use client";

import React from "react";
import { ArrowLeft, Calendar, Globe as Globe2, TrendingUp, Sparkles, CircleCheck as CheckCircle2, MapPin, ArrowUpRight } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
export default function CaseHero() {
  const shouldReduceMotion = useReducedMotion();
  const caseMetrics = [{
    label: "Client Profile",
    value: "Founded 2016",
    desc: "Brand-new UK digital magazine for young professionals",
    icon: Calendar
  }, {
    label: "Core Objective",
    value: "Readers = Revenue",
    desc: "Rank from scratch against high-competition national keywords via a local-first organic strategy",
    icon: Globe2
  }, {
    label: "Consultant Delivery",
    value: "Direct 1-to-1 Execution",
    desc: "Direct 1-to-1 delivery by Yassine Essaadi",
    icon: TrendingUp
  }];
  const deliverables = ["Local-to-national organic search progression", "High-intent keyword mapping for young professionals", "Technical site architecture & indexation overhaul", "Scalable editorial frameworks to drive sustainable reader revenue"];
  return <section id="casestudyhero" data-nav="light" className="relative overflow-hidden bg-background pt-8 pb-16 md:pt-12 md:pb-24 border-b border-border">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-secondary/40 via-background to-background" />

      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between pb-8">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/HomePage" className="text-muted-foreground hover:text-foreground">
                    Home
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/CaseStudiesPage" className="text-muted-foreground hover:text-foreground">
                    Case Studies
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage className="font-medium text-foreground">
                  Starting Local To Go Global
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div>
            <Button asChild variant="ghost" size="sm" className="gap-2 text-muted-foreground hover:text-foreground">
              <Link to="/CaseStudiesPage">
                <ArrowLeft className="size-4" />
                <span>Back to all case studies</span>
              </Link>
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-7 space-y-6">
            <div className="flex flex-wrap items-center gap-2.5">
              <Badge variant="secondary" className="px-3 py-1 font-medium bg-secondary text-secondary-foreground">
                <MapPin className="size-3.5 text-primary" />
                Case Study #01
              </Badge>
              <Badge variant="outline" className="px-3 py-1 text-muted-foreground border-border">
                Local-to-National Strategy
              </Badge>
              <span className="text-xs font-mono text-muted-foreground">
                Verified Client Engagement
              </span>
            </div>

            <motion.h1 initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            ease: "easeOut"
          }} className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-foreground tracking-tight leading-tight">
              Starting Local To Go Global: Scaling a New UK Online Magazine to National Search Authority
            </motion.h1>

            <motion.p initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1,
            ease: "easeOut"
          }} className="text-base sm:text-lg text-muted-foreground leading-relaxed font-sans">
              Launched in 2016 for UK young professionals, this brand-new online publication operated on a critical principle: <strong className="text-foreground font-semibold">Readers = Revenue</strong>. Facing high-competition national search queries with zero initial domain authority, I engineered an agile local-first organic strategy to build immediate topical trust before aggressively unlocking high-volume national search visibility and commercial ad monetization.
            </motion.p>

            <div className="space-y-3 pt-2">
              <p className="text-sm font-semibold text-foreground uppercase tracking-wide">
                Key Strategic Focus Areas
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {deliverables.map((item, i) => <div key={i} data-index={i} className="flex items-start gap-2 text-sm text-foreground">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </div>)}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-4 pt-4">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-md">
                <Link to="/ContactPage">
                  <span>Book Your Free SEO Consultation</span>
                  <ArrowUpRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-border hover:bg-secondary/40 text-foreground">
                <Link to="#case-breakdown">
                  <span>Explore Strategy Breakdown</span>
                </Link>
              </Button>
            </div>

            <div className="pt-2 text-xs text-muted-foreground">
              Yassine Essaadi — independent SEO consultant, Swindon, UK.
            </div>
          </div>

          <div className="lg:col-span-5">
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1,
            scale: 1
          } : {
            opacity: 0,
            scale: 0.98
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.6,
            delay: 0.15,
            ease: "easeOut"
          }}>
              <Card className="border border-border bg-card shadow-lg">
                <CardContent className="p-6 sm:p-8 space-y-6">
                  <div className="flex items-center justify-between pb-4 border-b border-border">
                    <div>
                      <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                        Consultancy Profile
                      </span>
                      <h2 className="text-lg font-bold text-foreground">
                        Project Overview &amp; Parameters
                      </h2>
                    </div>
                    <div className="p-2.5 rounded-lg bg-secondary text-primary">
                      <Sparkles className="size-5" />
                    </div>
                  </div>

                  <div className="space-y-4">
                    {caseMetrics.map((metric, i) => {
                    const IconComponent = metric.icon;
                    return <div key={i} data-index={i} className="flex items-start gap-4 p-3.5 rounded-lg bg-background border border-border/80 transition-colors hover:border-primary/40">
                          <div className="p-2 rounded-md bg-secondary/80 text-primary shrink-0">
                            <IconComponent className="size-4" />
                          </div>
                          <div className="space-y-0.5">
                            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                              {metric.label}
                            </span>
                            <p className="text-base font-semibold text-foreground">
                              {metric.value}
                            </p>
                            <p className="text-xs text-muted-foreground leading-relaxed">
                              {metric.desc}
                            </p>
                          </div>
                        </div>;
                  })}
                  </div>

                  <div className="rounded-lg bg-secondary/50 p-4 border border-border/60">
                    <p className="text-xs text-foreground leading-relaxed">
                      <strong className="font-semibold">Consultant Note:</strong> Genuine client engagement from YE Consultancy. Every roadmap, keyword model, and technical optimization was delivered 1-on-1 by Yassine Essaadi.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}